import { getAccessToken } from '../utils/authStorage';

/**
 * Indirection between apiClient and the Okta session.
 *
 * In the old app every single API call began with
 * `generateHeaderDetails(localStorage.getItem(tokenCacheKey), key)`, whose first act was
 * `validateAccessToken(...)` — refreshing an expired token, or throwing
 * TOKEN_NOT_ELIGIBLE_FOR_REFRESH when the session came from Empower (utils.js:32-48,
 * 50-58). Reading the cached token straight out of localStorage instead would drop that
 * refresh entirely: a user who left a tab open would start getting 401s where the old app
 * silently renewed.
 *
 * apiClient cannot import services/okta.ts directly, because the OktaAuth instance is
 * built from the runtime config that AuthContext fetches. So AuthContext registers the
 * resolver here once that config lands, and apiClient asks for a token through this
 * module. Before registration — and in tests — it falls back to the cached token, which
 * is what the very first request after a redirect login uses anyway.
 */

export type TokenResolver = () => Promise<string | null>;

const cachedTokenResolver: TokenResolver = async () => getAccessToken();

let resolver: TokenResolver = cachedTokenResolver;

export function setTokenResolver(next: TokenResolver): void {
  resolver = next;
}

/** Restores the default. Used by tests and by logout. */
export function resetTokenResolver(): void {
  resolver = cachedTokenResolver;
}

/**
 * Resolves the bearer token to attach to the next request.
 *
 * May REJECT with TOKEN_NOT_ELIGIBLE_FOR_REFRESH, which apiClient turns into the
 * "Sorry your session expired…" message — the same path the old app took.
 */
export function resolveAccessToken(): Promise<string | null> {
  return resolver();
}
